/**
 * The one canonical origin, and the indexing policy that goes with it.
 *
 * Every absolute URL the site emits comes from here: canonical links, Open
 * Graph, Twitter cards, the JSON-LD graph, the sitemap and robots.txt. Before
 * this module they were assembled from a `SITE_URL` that fell back to
 * `http://localhost:1234`, and the deployed site shipped that fallback: the
 * live homepage advertised `<link rel="canonical" href="http://localhost:1234">`
 * to search engines while also telling them `index, follow`.
 *
 * The value is substituted at build time rather than read at runtime. It has to
 * be byte-identical in the markup the server renders and in the bundle the
 * browser hydrates with, and the browser has no environment to read anyway.
 *
 * `VITE_SITE_URL` is public by design: it is a hostname that appears in the
 * page source. No other value from the root .env is exposed, and nothing
 * prefixed `VITE_` should ever hold a secret.
 */

declare const __SITE_URL__: string;
declare const __SITE_ENV__: string;

/** "production" publishes and indexes. Anything else is treated as staging. */
export type SiteEnv = "production" | "staging";

const raw = typeof __SITE_URL__ === "string" ? __SITE_URL__ : "";

export const siteEnv: SiteEnv = (typeof __SITE_ENV__ === "string" ? __SITE_ENV__ : "staging") === "production"
  ? "production"
  : "staging";

/**
 * Trailing slash removed so `${origin}${path}` never produces a double slash,
 * and so the homepage canonical is the bare origin rather than "origin/".
 */
export const siteOrigin = raw.replace(/\/+$/, "");

/** Historical name. Much of the content layer imports it from data/site.ts. */
export const siteUrl = siteOrigin;

export function isUsableOrigin(value: string): boolean {
  try {
    const url = new URL(value);
    return (url.protocol === "https:" || url.protocol === "http:") && Boolean(url.hostname);
  } catch {
    return false;
  }
}

/** True for the hostnames that must never reach a production build. */
export function isLocalOrigin(value: string): boolean {
  return /^https?:\/\/(localhost|127\.0\.0\.1|0\.0\.0\.0|\[::1\])(:|\/|$)/i.test(value);
}

/**
 * An absolute URL for anything the site publishes.
 *
 * Takes a root-relative path and returns it against the configured origin.
 * A value that is already absolute is returned untouched, so a source link to
 * the organisation's own site is not mangled into this domain.
 */
export function absoluteUrl(path: string): string {
  if (/^[a-z][a-z0-9+.-]*:/i.test(path)) return path;
  if (!siteOrigin) return path;
  const suffix = path === "/" ? "" : path.startsWith("/") ? path : `/${path}`;
  return `${siteOrigin}${suffix}`;
}

/**
 * What to tell crawlers.
 *
 * Staging is `noindex, nofollow`, without exception: a preview deployment that
 * is indexable competes with the real site for its own content. Production only
 * becomes indexable once the origin is a real absolute URL that is not
 * localhost, so a misconfigured build fails closed rather than publishing a
 * canonical nobody can reach.
 */
export const robotsDirective: string =
  siteEnv === "production" && isUsableOrigin(siteOrigin) && !isLocalOrigin(siteOrigin)
    ? "index, follow"
    : "noindex, nofollow";
